import { Dispatch, memo, SetStateAction, useCallback, useEffect, useRef } from "react"
import styled from "styled-components"

const InputBox = styled.input`
  width: 47px;
  height: 100%;
  border-radius: 6px;
  border: 1px solid ${(props) => props.theme.palette.mediumGray};
  background: #f4f5f7;
  text-align: center;

  &::placeholder {
    color: ${(props) => props.theme.palette.mediumGray};
  }
`

interface InputCodeProps {
  identifier: number
  value: string
  focusedId: number
  setFocusedInput: Dispatch<SetStateAction<number>>
  handleInputChange: (id: number, value: string) => void
  onPaste?: (pastedText: string) => void
}

const InputCode = (props: InputCodeProps) => {
  const { identifier, value, focusedId, setFocusedInput, handleInputChange, onPaste } = props

  const inputRef = useRef<HTMLInputElement>(null)

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      const char = newValue.slice(-1)

      handleInputChange(identifier, char)

      if (char) {
        setFocusedInput(identifier + 1)
      }
    },
    [identifier, setFocusedInput, handleInputChange]
  )

  const handlePasteEvent = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (!onPaste) return

      e.preventDefault()

      const pasted = e.clipboardData.getData("Text")

      onPaste(pasted)
    },
    [onPaste]
  )

  const handleFocus = useCallback(() => {
    setFocusedInput(identifier)
  }, [identifier])

  const handleBlur = useCallback(() => {
    setFocusedInput(-1)
  }, [])

  useEffect(() => {
    if (focusedId === identifier) {
      inputRef.current?.focus()
    }
  }, [focusedId, identifier])

  return (
    <InputBox
      ref={inputRef}
      name={`inputCode-${identifier}`}
      value={value}
      placeholder={focusedId === identifier ? "" : "-"}
      onChange={handleChange}
      onPaste={handlePasteEvent}
      onFocus={handleFocus}
      onBlur={handleBlur}
      autoComplete="off"
    />
  )
}

export default memo(InputCode)
